import { useEffect, useState } from "react";
import type { PaletteMode } from "@mui/material";
import type { View } from "./types";
import Layout from "./components/Layout";
import AuthGuard from "./AuthGuard";
import PlatformGuard from "./PlatformGuard";
import Dashboard from "./views/Dashboard";
import Drivers from "./views/Drivers";
import DriverDetail from "./views/DriverDetail";
import Tenants from "./views/Tenants";
import TenantDetail from "./views/TenantDetail";
import Memberships from "./views/Memberships";
import Companies from "./views/Companies";
import ApiKeys from "./views/ApiKeys";
import Settings from "./views/Settings";

const listViews: View[] = ["dashboard", "drivers", "tenants", "memberships", "companies", "api_keys", "settings"];

interface Route {
  view: View;
  id?: number;
}

function parseHash(): Route {
  const raw = window.location.hash.replace(/^#/, "");
  const slash = raw.indexOf("/");
  if (slash !== -1) {
    const viewPart = raw.slice(0, slash) as View;
    const idPart = parseInt(raw.slice(slash + 1), 10);
    if (!isNaN(idPart)) return { view: viewPart, id: idPart };
  }
  const view = raw as View;
  return { view: listViews.includes(view) ? view : "dashboard" };
}

interface Props {
  themeMode: PaletteMode;
  onToggleThemeMode: () => void;
}

export default function App({ themeMode, onToggleThemeMode }: Props) {
  const [route, setRoute] = useState<Route>(parseHash);

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const navigate = (v: View, id?: number) => {
    window.location.hash = id != null ? `${v}/${id}` : v;
    setRoute({ view: v, id });
  };

  return (
    <AuthGuard>
      <PlatformGuard>
        <Layout
          view={route.view}
          onNavigate={navigate}
          themeMode={themeMode}
          onToggleThemeMode={onToggleThemeMode}
        >
          {route.view === "dashboard" && <Dashboard />}
          {route.view === "drivers" && <Drivers onOpenDetail={(id) => navigate("driver", id)} />}
          {route.view === "driver" && route.id != null && (
            <DriverDetail id={route.id} onBack={() => navigate("drivers")} onOpenDriver={(id) => navigate("driver", id)} onOpenTenant={(id) => navigate("tenant", id)} />
          )}
          {route.view === "tenants" && <Tenants onOpenDetail={(id) => navigate("tenant", id)} />}
          {route.view === "tenant" && route.id != null && (
            <TenantDetail id={route.id} onBack={() => navigate("tenants")} onOpenDriver={(id) => navigate("driver", id)} />
          )}
          {route.view === "memberships" && <Memberships onOpenDriver={(id) => navigate("driver", id)} onOpenTenant={(id) => navigate("tenant", id)} />}
          {route.view === "companies" && <Companies />}
          {route.view === "api_keys" && <ApiKeys />}
          {route.view === "settings" && <Settings />}
        </Layout>
      </PlatformGuard>
    </AuthGuard>
  );
}
